import { pretty, toggleIn } from '@/features/menu/utils/product-helpers';
import { useT } from '@/i18n';
import { useState } from 'react';
import { Block, Chip, f7, List, ListItem, Popover } from 'framework7-react';

// ─── Inline code-set picker (allergens / labels) ─────────────────────────────
// A summary row that opens a popover checklist anchored to itself, so codes are
// picked in place without pushing a separate page. Toggling is local; the parent
// is notified once, on popover close, so a multi-pick is a single save.
export default function CodePopoverSelect<T extends string>({
  title,
  all,
  selected,
  onChange,
  popoverClass,
}: {
  title: string;
  all: readonly T[];
  selected: readonly T[];
  onChange: (codes: T[]) => void;
  popoverClass: string;
}) {
  const t = useT();
  const [sel, setSel] = useState<T[]>([...selected]);

  const open = (e: React.MouseEvent) => {
    // Re-sync from the source of truth each open - the product may have changed.
    setSel([...selected]);
    f7.popover.open(`.${popoverClass}`, e.currentTarget as HTMLElement);
  };

  const commit = () => {
    const a = JSON.stringify([...sel].sort());
    const b = JSON.stringify([...selected].sort());
    if (a !== b) onChange([...sel].sort() as T[]);
  };

  return (
    <>
      <List strongIos insetIos dividersIos>
        <ListItem
          title={title}
          after={selected.length ? t.menu.selectedCount(selected.length) : t.menu.noSelection}
          link="#"
          onClick={open}
        />
      </List>
      {selected.length > 0 && (
        <Block>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {selected.map((c) => (
              <Chip key={c} text={pretty(c)} outline />
            ))}
          </div>
        </Block>
      )}
      <Popover className={popoverClass} onPopoverClosed={commit}>
        <List
          strongIos
          dividersIos
          style={{ margin: 0, maxHeight: 360, overflowY: 'auto', width: 260 }}
        >
          {all.map((code) => {
            const on = sel.includes(code);
            return (
              <ListItem
                key={code}
                title={pretty(code)}
                link="#"
                noChevron
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  setSel((cur) => toggleIn(cur, code));
                }}
              >
                {on && (
                  <i
                    slot="after"
                    className="f7-icons"
                    style={{ color: 'var(--f7-theme-color)', fontSize: 22 }}
                  >
                    checkmark
                  </i>
                )}
              </ListItem>
            );
          })}
        </List>
      </Popover>
    </>
  );
}
